import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SECURITY NOTE: Using localStorage for token storage
// ⚠️  localStorage is vulnerable to XSS attacks. For production, consider:
// 1. HttpOnly cookies with SameSite=Strict
// 2. Content Security Policy (CSP) headers
// 3. Input sanitization to prevent XSS
// Current implementation is acceptable for MVP but should be upgraded for production
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const saveSession = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
  };

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          saveSession(savedToken, response.data);
        } catch (error) {
          const status = error.response?.status;
          const cachedUser = JSON.parse(localStorage.getItem('user') || 'null');
          if (status === 401 || status === 403 || !cachedUser) {
            clearSession();
          } else {
            // Network/server hiccup: keep the session instead of forcing a new login
            setToken(savedToken);
            setUser(cachedUser);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    saveSession(access_token, userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    saveSession(access_token, newUser);
    return newUser;
  };

  const logout = clearSession;

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
